import { Profiler, type ProfilerOnRenderCallback, type ReactNode } from 'react';

export const renderProfilerStorageKey = 'forma:renderProfiler';

const enabledProfilerValues = new Set(['1', 'true', 'debug', 'on', 'yes']);
const maxStoredRenderEvents = 500;

type ReadableStorage = Pick<Storage, 'getItem'>;
type WritableStorage = Pick<Storage, 'getItem' | 'removeItem' | 'setItem'>;

export type RenderProfilerEvent = {
  actualDurationMs: number;
  baseDurationMs: number;
  commitTimeMs: number;
  label: string;
  phase: Parameters<ProfilerOnRenderCallback>[1];
  route: string;
  startTimeMs: number;
  timestamp: string;
};

const renderProfilerEvents: RenderProfilerEvent[] = [];

function readProfilerFlag(storage: ReadableStorage | null) {
  if (!storage) {
    return null;
  }

  try {
    return storage.getItem(renderProfilerStorageKey);
  } catch {
    return null;
  }
}

export function isRenderProfilerEnabled(
  storage: ReadableStorage | null = typeof localStorage === 'undefined'
    ? null
    : localStorage,
) {
  const flag = readProfilerFlag(storage);

  return flag ? enabledProfilerValues.has(flag.trim().toLowerCase()) : false;
}

export function setRenderProfilerEnabled(
  enabled: boolean,
  storage: WritableStorage | null = typeof localStorage === 'undefined'
    ? null
    : localStorage,
) {
  if (!storage) {
    return false;
  }

  try {
    if (enabled) {
      storage.setItem(renderProfilerStorageKey, '1');
    } else {
      storage.removeItem(renderProfilerStorageKey);
    }

    return true;
  } catch {
    return false;
  }
}

function storeRenderProfilerEvent(event: RenderProfilerEvent) {
  renderProfilerEvents.push(event);

  if (renderProfilerEvents.length > maxStoredRenderEvents) {
    renderProfilerEvents.splice(
      0,
      renderProfilerEvents.length - maxStoredRenderEvents,
    );
  }
}

export function getRenderProfilerEvents() {
  return [...renderProfilerEvents];
}

export function getRenderProfilerEventCount() {
  return renderProfilerEvents.length;
}

export function clearRenderProfilerEvents() {
  renderProfilerEvents.length = 0;
}

export function serializeRenderProfilerEvents() {
  return JSON.stringify(getRenderProfilerEvents(), null, 2);
}

function getCurrentRouteLabel() {
  if (typeof window === 'undefined') {
    return 'server';
  }

  return window.location.pathname || '/';
}

export function recordRenderProfilerCommit({
  actualDuration,
  baseDuration,
  commitTime,
  label,
  phase,
  startTime,
}: {
  actualDuration: number;
  baseDuration: number;
  commitTime: number;
  label: string;
  phase: Parameters<ProfilerOnRenderCallback>[1];
  startTime: number;
}) {
  if (!isRenderProfilerEnabled()) {
    return;
  }

  const event = {
    actualDurationMs: Number(actualDuration.toFixed(2)),
    baseDurationMs: Number(baseDuration.toFixed(2)),
    commitTimeMs: Number(commitTime.toFixed(2)),
    label,
    phase,
    route: getCurrentRouteLabel(),
    startTimeMs: Number(startTime.toFixed(2)),
    timestamp: new Date().toISOString(),
  } satisfies RenderProfilerEvent;

  storeRenderProfilerEvent(event);
  console.debug('[forma-render]', event);
}

const handleRenderProfilerCommit: ProfilerOnRenderCallback = (
  id,
  phase,
  actualDuration,
  baseDuration,
  startTime,
  commitTime,
) => {
  recordRenderProfilerCommit({
    actualDuration,
    baseDuration,
    commitTime,
    label: id,
    phase,
    startTime,
  });
};

export function RenderProfilerRoot({ children }: { children: ReactNode }) {
  return <RenderProfilerScope label="app-root">{children}</RenderProfilerScope>;
}

export function RenderProfilerScope({
  children,
  label,
}: {
  children: ReactNode;
  label: string;
}) {
  if (!import.meta.env.DEV) {
    return children;
  }

  return (
    <Profiler id={label} onRender={handleRenderProfilerCommit}>
      {children}
    </Profiler>
  );
}
